import { BulkOperationProps } from '@type/bulk-operations';
import { useState } from 'react';
import { useBjForm } from '@hooks/useBjForm';
import { useTranslation } from 'next-i18next';
import { useSwrBulkCopyApplicants } from '@hooks/useSwrApi/endpoints/bulk-operations';
import { Modal, Button } from '@components/common';
import { FolderPlusIcon, PlusIcon, BriefcaseIcon } from '@heroicons/react/20/solid';
import { i18nConfig } from 'i18n-config';
import { useOpenBulkModal } from './hooks/useOpenBulkModal';
import { JobListSelector } from './JobListSelector';

interface BulkCopyApplicantsProps extends BulkOperationProps {
  hideTriggerIcon?: boolean;
  triggerDisabled?: boolean;
}

/**
 * @description Copy applicants to another job.
 *
 * - **getSelectedApplicants**: Get the selected applicants. Function that returns an array of selected applicants.
 * - **selectAll**: Boolean value that indicates if all applicants are selected.
 * - **status**: Status of the applicants (ie: interview, new, etc.).
 * - **jobSlug**: The job slug.
 * - **locale**: The locale
 * - **queryParams**: The filters query params object.
 */
export const BulkCopyApplicants = (props: BulkCopyApplicantsProps) => {
  const {
    getSelectedApplicants,
    jobSlug = '',
    locale = i18nConfig.defaultLocale,
    selectAll,
    selectByColumn,
    status,
    queryParams,
    normalizeSelectedApplicantsForBulkAction,
    hideTriggerIcon = false,
    triggerDisabled = false,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Show existing jobs or add new job. Undefined means we don't know if we have a job list or not. When we know it will become initially false.
  const [showJobList, setShowJobList] = useState<boolean | undefined>(undefined);


  // Handle the bulk applicants modal toggle and base post object build
  const {
    isOpen,
    toggleBulkModal,
    closeOnSuccess,
    basePostObject,
  } = useOpenBulkModal({
    getSelectedApplicants, selectAll, status, queryParams, selectByColumn, normalizeSelectedApplicantsForBulkAction,
  });


  // BJ Form
  const {
    handleCallbackSubmit,
    record,
    updateFormElements,
  } = useBjForm({
    wizardData: basePostObject || {},
  });


  // SWR: Send group message
  const {
    trigger,
    isMutating,
  } = useSwrBulkCopyApplicants(jobSlug, locale, closeOnSuccess);


  // Render component
  // **************************************
  return (
    <>
      {/* TRIGGER BUTTON */}
      <Button
        styling="text"
        size="sm"
        color="ink"
        className="grow px-4"
        disabled={triggerDisabled}
        onClick={toggleBulkModal}
        aria-label={t('cv.action.add')}
      >
        {!hideTriggerIcon && <FolderPlusIcon className="mr-2 size-5 sm:mr-1" />}
        <span>{t('cv.action.add')}</span>
      </Button>


      {/* MODAL COPY APPLICANTS */}
      <Modal isOpen={isOpen} onClose={toggleBulkModal} size="sm" className="!overflow-visible">
        <form
          noValidate
          onSubmit={(event) => handleCallbackSubmit(event, (data) => {
            // Read destinationJobId directly from the form to avoid useBjForm timing/nullable issues
            const formEl = event.target as HTMLFormElement;
            const rawJobId = new FormData(formEl).get('destinationJobId');
            const destinationJobId = rawJobId ? Number(rawJobId) : undefined;

            return trigger({
              ...data,
              ...(destinationJobId != null && !Number.isNaN(destinationJobId) && { destinationJobId }),
            });
          })}
        >

          {/* Modal header */}
          <Modal.Header onClose={toggleBulkModal}>
            {t('cv.action.resume_copy.title')}
          </Modal.Header>

          {/* Modal body */}
          <Modal.Body className="bg-surface-100">
            {/* Select job */}
            <JobListSelector
              record={record}
              locale={locale}
              showJobList={showJobList}
              setShowJobList={setShowJobList}
              updateFormElements={updateFormElements}
            />
          </Modal.Body>

          {/* Modal footer */}
          <Modal.Footer className={`flex ${showJobList === undefined ? 'justify-end' : 'justify-between'} justify-between`}>
            { /* Switch to add new job */
              showJobList && (
                <Button
                  styling="outline"
                  size="sm"
                  rounding="full"
                  color="light"
                  onClick={() => setShowJobList(false)}
                >
                  <PlusIcon className="mr-1 size-5" />
                  {t('cv.action.new')}
                </Button>
              )
            }

            { /* Switch to see existing jobs */
              showJobList === false && (
                <Button
                  styling="outline"
                  size="sm"
                  rounding="full"
                  color="light"
                  onClick={() => setShowJobList(true)}
                >
                  <BriefcaseIcon className="mr-1.5 size-5" />
                  {t('cv.action.existing_job')}
                </Button>
              )
            }

            {/* Submit */}
            <Button
              type="submit"
              color="secondary"
              rounding="full"
              size="sm"
              className="!px-7"
              isLoading={isMutating}
            >
              <FolderPlusIcon className="mr-1.5 size-5" />
              {t('global.action.copy')}
            </Button>
          </Modal.Footer>

        </form>
      </Modal>
    </>
  );
};
